import { defineComponent as _defineComponent } from 'vue'
import type Provider from '@/interfaces/provider';
import type { HorizontalTitleListItem } from '@/components/timeline/types';

import {
	ObjectType,
	Platform,
	PopularTitlesSorting,
	PopularityRankTypes,
	StreamingChartObjectType,
} from '@/@types/graphql-types';
import { PopularTabType } from '@/interfaces/titles';

import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getSelectedTab } from '@/components/popular/provider-module/helper';
import Top10HorizontalTitleListGraphql from '@/components/popular/provider-module/Top10HorizontalTitleListGraphql.vue';

import {
	GetProviderTop10TitlesDocument,
	type GetProviderTop10TitlesQuery,
	type GetProviderTop10TitlesQueryVariables,
} from './queries/GetProviderTop10Titles.query';

import {
	GetProviderTop10TitlesFallbackDocument,
	type GetProviderTop10TitlesFallbackQuery,
	type GetProviderTop10TitlesFallbackQueryVariables,
} from './queries/GetProviderTop10TitlesFallback.query';

import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';

import { useQuery } from '@/helpers/composables/useApollo';
import { useI18n } from '@/helpers/composables/useI18n';

import { computed } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'Top10Titles',
  props: {
    provider: null,
    selectedTab: null,
    showTitle: { type: Boolean, default: true },
    showMobileTitle: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props as { provider: Provider, selectedTab: PopularTabType, showTitle: boolean, showMobileTitle: boolean };



const { language, country } = storeToRefs(useLanguageStore());
const { activeRoute } = storeToRefs(useRoutingStore());
const { t } = useI18n();

useProvideClickoutPlacement('top_10');

const top10TitlesObjectTypes = computed(() => {
	switch (props.selectedTab) {
		case PopularTabType.MOVIE:
			return [ObjectType.Movie];

		case PopularTabType.SHOW:
			return [ObjectType.Show];

		case PopularTabType.ALL:
			return [ObjectType.Show, ObjectType.Movie];

		default:
			return [];
	}
});

const top10ProviderMovieTitles = useQuery<GetProviderTop10TitlesQuery, GetProviderTop10TitlesQueryVariables>(
	GetProviderTop10TitlesDocument,
	() => ({
		after: '',
		watchNowFilter: {
			packages: [props.provider.shortName],
		},
		offerFilter: {
			packages: [props.provider.shortName],
			bestOnly: true,
		},
		language: language.value,
		country: country.value,
		first: 10,
		platform: Platform.Web,
		streamingChartsFilter: {
			objectType: StreamingChartObjectType.Movie,
			category: PopularityRankTypes.WeeklyPopularitySameContentType,
			nextTitles: 0,
			previousTitles: 0,
			packages: [props.provider.shortName],
		},
	}),
	{ fetchPolicy: 'cache-and-network' }
);

const top10ProviderShowTitles = useQuery<GetProviderTop10TitlesQuery, GetProviderTop10TitlesQueryVariables>(
	GetProviderTop10TitlesDocument,
	() => ({
		after: '',
		watchNowFilter: {
			packages: [props.provider.shortName],
		},
		offerFilter: {
			packages: [props.provider.shortName],
			bestOnly: true,
		},
		language: language.value,
		country: country.value,
		first: 10,
		platform: Platform.Web,
		streamingChartsFilter: {
			objectType: StreamingChartObjectType.Show,
			category: PopularityRankTypes.WeeklyPopularitySameContentType,
			nextTitles: 0,
			previousTitles: 0,
			packages: [props.provider.shortName],
		},
	}),
	{ fetchPolicy: 'cache-and-network' }
);

const top10FallbackTitles = useQuery<GetProviderTop10TitlesFallbackQuery, GetProviderTop10TitlesFallbackQueryVariables>(
	GetProviderTop10TitlesFallbackDocument,
	() => ({
		after: '',
		popularTitlesFilter: {
			packages: [props.provider.shortName],
			objectTypes: top10TitlesObjectTypes.value,
		},
		watchNowFilter: {
			packages: [props.provider.shortName],
		},
		offerFilter: {
			packages: [props.provider.shortName],
			bestOnly: true,
			preAffiliate: true,
		},
		popularTitlesSortBy: PopularTitlesSorting.Trending,
		language: language.value,
		country: country.value,
		first: 10,
		platform: Platform.Web,
		streamingChartsFilter: {
			category: PopularityRankTypes.WeeklyPopularitySameContentType,
			objectType:
				top10TitlesObjectTypes.value.length === 1 ? (top10TitlesObjectTypes.value[0] as any) : undefined,
			nextTitles: 0,
			previousTitles: 0,
		},
	}),
	{ fetchPolicy: 'cache-and-network' }
);

const isLoading = computed(
	() =>
		top10ProviderMovieTitles.loading.value ||
		top10ProviderShowTitles.loading.value ||
		top10FallbackTitles.loading.value
);

const top10MixedTitles = computed(() => {
	const mixedTitles = top10ProviderMovieTitles?.result.value?.streamingCharts.edges?.concat(
		top10ProviderShowTitles?.result.value?.streamingCharts.edges || []
	);

	const sortedMixedTitles = mixedTitles?.sort((a: any, b: any) => {
		if (a?.streamingChartInfo?.rank && b?.streamingChartInfo?.rank) {
			const rankScoreA = a?.streamingChartInfo?.rank * 0.99;
			const rankScoreB = b?.streamingChartInfo?.rank * 0.99;
			const daysInTop10ScoreA = a?.streamingChartInfo?.daysInTop10 * 0.01;
			const daysInTop10ScoreB = b?.streamingChartInfo?.daysInTop10 * 0.01;

			const scoreA = rankScoreA + daysInTop10ScoreA;
			const scoreB = rankScoreB + daysInTop10ScoreB;

			a = {
				...a,
				score: scoreA,
			};

			b = {
				...b,
				score: scoreB,
			};

			return a.score - b.score;
		}
		return 0;
	});

	return sortedMixedTitles?.slice(0, 10);
});

const titleNodes = computed(() => {
	if (props.selectedTab === PopularTabType.MOVIE) {
		return top10ProviderMovieTitles.result.value?.streamingCharts.edges?.map((el: { node: any }) => el.node);
	}

	if (props.selectedTab === PopularTabType.SHOW) {
		return top10ProviderShowTitles.result.value?.streamingCharts.edges?.map((el: { node: any }) => el.node);
	}

	return top10MixedTitles.value?.map((el: { node: any }) => el.node);
});

const fallbackTitleNodes = computed(() => {
	return top10FallbackTitles.result.value?.popularTitles.edges
		?.map((el: { node: any }) => el.node)
		.sort(
			(
				a: { streamingCharts: { edges: { streamingChartInfo: { rank: number } }[] } },
				b: { streamingCharts: { edges: { streamingChartInfo: { rank: number } }[] } }
			) => {
				const position_a = a.streamingCharts?.edges?.[0]?.streamingChartInfo?.rank || 0;
				const position_b = b.streamingCharts?.edges?.[0]?.streamingChartInfo?.rank || 0;
				return position_a - position_b;
			}
		);
});

const titlesToDisplay = computed(() => (titleNodes.value?.length !== 10 ? fallbackTitleNodes.value : titleNodes.value));

const titleText = computed(() => {
	switch (props.selectedTab) {
		case PopularTabType.ALL:
			return t('WEBAPP_PROVIDER_PAGE_TOP_TITLES', {
				provider: props.provider.clearName,
				number: titlesToDisplay.value?.length ?? 10,
			}).toString();
		case PopularTabType.MOVIE:
			return t('WEBAPP_PROVIDER_PAGE_TOP_MOVIES', {
				provider: props.provider.clearName,
				number: titlesToDisplay.value?.length ?? 10,
			}).toString();
		case PopularTabType.SHOW:
			return t('WEBAPP_PROVIDER_PAGE_TOP_TV_SHOWS', {
				provider: props.provider.clearName,
				number: titlesToDisplay.value?.length ?? 10,
			}).toString();
		default:
			return '';
	}
});

function trackTitleClick({ index, title }: { index: number; title: HorizontalTitleListItem }) {
	const selectedTab = getSelectedTab(activeRoute?.value.name);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: 'top-10-titles',
			property: selectedTab,
			value: index,
		},
		[new SnowplowTitleContextGraphql(title.objectId, title.objectType)]
	);
}

return { __sfc: true,props, language, country, activeRoute, t, top10TitlesObjectTypes, top10ProviderMovieTitles, top10ProviderShowTitles, top10FallbackTitles, isLoading, top10MixedTitles, titleNodes, fallbackTitleNodes, titlesToDisplay, titleText, trackTitleClick, Top10HorizontalTitleListGraphql }
}

})